import { useRef } from "react";
import { Plus, Trash2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { btn } from "@/components/common/btn";
import { money } from "@/lib/format";

const COLS = ["qty", "part", "vendor", "stock", "cost", "gm", "price"];
const cell = "h-7 text-sm border-transparent bg-transparent px-1.5 shadow-none hover:border-slate-200 focus-visible:border-brand focus-visible:ring-0";

// Parts quote grid (Idea A): proposal-style sheet. New line lands on top; Enter on the last cell of a
// row adds another; ↑/↓ move between rows in the same column. Footer carries cost / GM% / total live.
export function PartsQuoteGrid({ lines, updateLineItem, addLine, removeLine, freight = 0, taxAmt = 0, taxRate = 0, total = 0, readOnly = false }) {
  const grid = useRef(null);

  const focusCell = (row, col) => {
    const el = grid.current?.querySelector(`[data-cell="${row}-${col}"]`);
    if (el) { el.focus(); el.select?.(); }
  };
  const onKey = (e, idx, col) => {
    const ci = COLS.indexOf(col);
    if (e.key === "ArrowDown") { e.preventDefault(); focusCell(idx + 1, col); }
    else if (e.key === "ArrowUp") { e.preventDefault(); focusCell(idx - 1, col); }
    else if (e.key === "Enter") {
      e.preventDefault();
      if (ci < COLS.length - 1) focusCell(idx, COLS[ci + 1]);
      else if (idx === lines.length - 1) { addLine(); setTimeout(() => focusCell(0, "qty"), 0); }
      else focusCell(idx + 1, "qty");
    }
  };

  const material = lines.reduce((s, li) => s + (li.total || 0), 0);
  const cost = lines.reduce((s, li) => s + (parseFloat(li.qty) || 0) * (parseFloat(li.unit_cost) || 0), 0);
  const gm = material > 0 ? ((material - cost) / material) * 100 : 0;

  return (
    <div className="data-table-container" ref={grid} data-testid="parts-quote-grid">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-[11px] text-slate-500">
            <th className="px-2 py-1.5 text-left font-semibold w-14">Qty</th>
            <th className="px-2 py-1.5 text-left font-semibold">Part / Description</th>
            <th className="px-2 py-1.5 text-left font-semibold w-32">Vendor</th>
            <th className="px-2 py-1.5 text-center font-semibold w-16">Stock</th>
            <th className="px-2 py-1.5 text-right font-semibold w-24">Cost</th>
            <th className="px-2 py-1.5 text-right font-semibold w-16">GM %</th>
            <th className="px-2 py-1.5 text-right font-semibold w-24">Price</th>
            <th className="px-2 py-1.5 text-right font-semibold w-28">Total</th>
            <th className="w-8 px-1">
              {!readOnly && (
                <button type="button" onClick={() => { addLine(); setTimeout(() => focusCell(0, "part"), 0); }} title="Add line (lands on top)" data-testid="add-line"
                  className="inline-flex h-6 w-6 items-center justify-center rounded-md bg-emerald-600 text-white hover:bg-emerald-700">
                  <Plus className="w-3.5 h-3.5" />
                </button>
              )}
            </th>
          </tr>
        </thead>
        <tbody>
          {lines.map((li, idx) => (
            // eslint-disable-next-line react/no-array-index-key -- rows are positional; handlers are index-based
            <tr key={`pc-line-${idx}`} className={`border-t ${idx === 0 && !li.part_name ? "bg-emerald-50/40" : ""}`} data-testid={`line-item-${idx}`}>
              <td className="px-1 py-0.5"><Input type="number" value={li.qty} data-cell={`${idx}-qty`} onKeyDown={(e) => onKey(e, idx, "qty")} onChange={e => updateLineItem(idx, "qty", parseFloat(e.target.value) || 0)} className={`${cell} text-center num`} data-testid={`qty-${idx}`} /></td>
              <td className="px-1 py-0.5"><Input value={li.part_name} data-cell={`${idx}-part`} onKeyDown={(e) => onKey(e, idx, "part")} onChange={e => updateLineItem(idx, "part_name", e.target.value)} placeholder="Part name / description" className={cell} data-testid={`part-name-${idx}`} /></td>
              <td className="px-1 py-0.5"><Input value={li.vendor || ""} data-cell={`${idx}-vendor`} onKeyDown={(e) => onKey(e, idx, "vendor")} onChange={e => updateLineItem(idx, "vendor", e.target.value)} placeholder={li.is_stock ? "—" : "Vendor"} disabled={li.is_stock} className={cell} data-testid={`line-vendor-${idx}`} /></td>
              <td className="px-1 py-0.5 text-center">
                <button type="button" data-cell={`${idx}-stock`} onKeyDown={(e) => onKey(e, idx, "stock")} onClick={() => updateLineItem(idx, "is_stock", !li.is_stock)} disabled={readOnly} data-testid={`line-stock-${idx}`}
                  title={li.is_stock ? "Ships from stock — excluded from vendor PO" : "Order from vendor"}
                  className={`rounded-full px-2 py-0.5 text-[10px] font-semibold border ${li.is_stock ? "bg-emerald-100 text-emerald-700 border-emerald-300" : "bg-slate-100 text-slate-500 border-slate-200"}`}>
                  {li.is_stock ? "Stock" : "Order"}
                </button>
              </td>
              <td className="px-1 py-0.5"><Input type="number" step="0.01" value={li.unit_cost ?? 0} data-cell={`${idx}-cost`} onKeyDown={(e) => onKey(e, idx, "cost")} onChange={e => updateLineItem(idx, "unit_cost", parseFloat(e.target.value) || 0)} className={`${cell} text-right num`} data-testid={`unit-cost-${idx}`} /></td>
              <td className="px-1 py-0.5"><Input type="number" step="0.1" value={li.margin_pct ?? 50} data-cell={`${idx}-gm`} onKeyDown={(e) => onKey(e, idx, "gm")} onChange={e => updateLineItem(idx, "margin_pct", parseFloat(e.target.value) || 0)} className={`${cell} text-right num`} data-testid={`margin-${idx}`} /></td>
              <td className="px-1 py-0.5"><Input type="number" step="0.01" value={li.unit_price ?? 0} data-cell={`${idx}-price`} onKeyDown={(e) => onKey(e, idx, "price")} onChange={e => updateLineItem(idx, "unit_price", parseFloat(e.target.value) || 0)} className={`${cell} text-right num`} title="Auto from cost × GM — editable" data-testid={`unit-price-${idx}`} /></td>
              <td className="px-2 py-0.5 text-right num font-medium" data-testid={`line-total-${idx}`}>{money(li.total || 0)}</td>
              <td className="px-1 py-0.5 text-center">
                {lines.length > 1 && !readOnly && (
                  <button type="button" aria-label="Remove line" onClick={() => removeLine(idx)} className="text-slate-300 hover:text-red-500" data-testid={`remove-line-${idx}`}><Trash2 className="w-3.5 h-3.5" /></button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="flex items-center gap-5 px-3 py-2 border-t text-xs text-slate-600 flex-wrap" data-testid="parts-grid-footer">
        <span>Material <b className="num text-slate-900" data-testid="grid-material">{money(material)}</b></span>
        {freight > 0 && <span>Freight <b className="num text-slate-900">{money(freight)}</b></span>}
        {taxAmt > 0 && <span>Tax {taxRate}% <b className="num text-slate-900">{money(taxAmt)}</b></span>}
        <span className="ml-auto">Cost <b className="num text-slate-900" data-testid="grid-cost">{money(cost)}</b></span>
        <span>GM <b className={`num ${gm >= 40 ? "text-emerald-700" : gm >= 25 ? "text-amber-600" : "text-red-600"}`} data-testid="grid-gm">{gm.toFixed(1)}%</b></span>
        <span className="text-base font-semibold text-slate-900 num" data-testid="grid-total">Total {money(total)}</span>
      </div>
    </div>
  );
}
